import { UserStatus } from "@/components/user-avatar/types";
import { debounce } from "@/utils/debounce";
import { Status } from "@/hooks/use-status/types";
import { getStatuses } from "./api";

const BATCHING_DELAY = 50; // assume that we should only batch requests when we render list of user avatar components

type ResolveFunction = (value: Status) => void;
type RejectFunction = (reason?: Error) => void;

type StatusRequest = {
  resolve: ResolveFunction;
  reject: RejectFunction;
};

class StatusRequestBatcher {
  private requestedEmails = new Map<string, Map<string, StatusRequest[]>>();

  private debouncedGetStatuses = debounce(
    (statusUrl: string, userIds: string[]) => {
      return getStatuses(statusUrl, userIds)
        .then((statuses: Status[]) => {
          // process all requests for each userId
          for (const userId of userIds) {
            const emailRequests = this.requestedEmails.get(statusUrl);

            if (emailRequests) {
              const requests = emailRequests.get(userId);

              if (requests) {
                for (const request of requests) {
                  const status = statuses.find(
                    status =>
                      status.email?.toLowerCase() === userId?.toLowerCase(),
                  ) || {
                    availability: UserStatus.PresenceUnknown,
                    email: userId,
                    activity: "",
                  };

                  request.resolve(status);
                }
              }
            }
          }
        })
        .catch((error: Error) => {
          // process all requests for each userId
          const emailRequests = this.requestedEmails.get(statusUrl);

          if (emailRequests) {
            for (const userId of emailRequests.keys()) {
              const requests = emailRequests.get(userId);

              if (requests) {
                for (const request of requests) {
                  request.reject(error);
                }
              }
            }
          }
        })
        .finally(() => {
          userIds.forEach(userId => {
            const emailRequests = this.requestedEmails.get(statusUrl);

            if (emailRequests) {
              emailRequests.delete(userId);
            }
          });
        });
    },
    BATCHING_DELAY,
  );

  getStatus(statusUrl: string, userId: string): Promise<Status> {
    return new Promise((resolve, reject) => {
      // if it is first request for this statusUrl, create a new map
      if (!this.requestedEmails.has(statusUrl)) {
        this.requestedEmails.set(statusUrl, new Map<string, StatusRequest[]>());
      }

      // if this first request for this userId, create a new array
      const emailRequests = this.requestedEmails.get(statusUrl);

      if (emailRequests && !emailRequests.has(userId)) {
        emailRequests.set(userId, []);
      }

      // save resolve and reject functions to process after batch request is done
      const request: StatusRequest = {
        resolve,
        reject,
      };

      // save this request to the map
      if (emailRequests) {
        const requests = emailRequests.get(userId);

        if (requests) {
          requests.push(request);
        }
      }

      // collect all userIds that are requested
      const userIds = Array.from(emailRequests?.keys() || []);

      // run debounced batch request
      this.debouncedGetStatuses(statusUrl, userIds);
    });
  }
}

export const statusRequestBatcher = new StatusRequestBatcher();
